/**
 * Shared definitions for the four DATA 360 pricing modules.
 *
 * The master/detail ProductSelector and each module detail pane read from here
 * so the modules share one source of truth for their labels, volume drivers,
 * onboarding-row shape, and the field names sent to calculate-price. Field names
 * mirror the backend SourceRow contract and the CRM discovery questionnaire.
 */

export const MODULE_KEYS = [
  'materialTraceability',
  'pipeFitter',
  'ndt',
  'coating',
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

/** Arrival-type values must match the backend nre_config difficultyMultipliers keys. */
export const ARRIVAL_TYPES: { value: string; label: string }[] = [
  { value: 'nativeDigital', label: 'Native digital' },
  { value: 'api', label: 'API' },
  { value: 'spreadsheet', label: 'Spreadsheet / CSV' },
  { value: 'scannedPdf', label: 'Scanned PDF' },
  { value: 'handwritten', label: 'Handwritten paper' },
];

export const CAPTURE_PATHS: { value: string; label: string }[] = [
  { value: 'A', label: 'A - CRCE service crew' },
  { value: 'B', label: 'B - Instrumented bolt-on' },
  { value: 'C', label: 'C - Contractor BYOD upload' },
];

export interface ModuleSourceRow {
  id: string;
  name: string;
  sourceArrivalType: string;
  dataTypes: string[];
  /**
   * Whether this source contributes its one-time onboarding NRE. Defaults on
   * (undefined or true charges it). A rep unchecks it for a source already
   * onboarded on a past project, to skip that source's onboarding fee.
   */
  chargeNre?: boolean;
  nreOverride?: number;
  capturePath?: string;
  serviceDays?: number;
  /** Coating only: this contractor's field-capture mode (service / customer / byod / none). */
  captureMode?: string;
  /** Coating only: this contractor's bolt-on hardware, priced like a welding deal. */
  boltOnMachineCount?: number;
  boltOnSupportHours?: number;
  boltOnTrainingPeople?: number;
  boltOnMobilize?: boolean;
  /** Material Traceability only: pipes onboarded from this mill. Drives the per-mill per-pipe charge. */
  pipeCount?: number;
  /** Material Traceability only: mill test reports per pipe. Falls back to the global default when absent. */
  reportsPerPipe?: number;
  /** Material Traceability only: minutes to clear one exception document. Falls back to the global default when absent. */
  minutesPerException?: number;
  /**
   * Material Traceability only: per-mill auto-extract rate as a fraction [0, 1].
   * Resolution order: this value -> the format-derived rate -> the global default.
   */
  autoExtractRate?: number;
}

export interface ModuleState {
  isSelected: boolean;
  volume?: number;
  sources?: ModuleSourceRow[];
  options?: Record<string, unknown>;
  /** Per-proposal cost-input overrides; any key absent falls back to the global default. */
  overrides?: Record<string, number>;
  /** Service tier (for example storage_only / extended) where the module offers one. */
  tier?: string;
  /** Field-capture mode (service / customer / byod / none) for PipeFitter and Coating. */
  captureMode?: string;
  /** PipeFitter algorithm gain-share inputs the rep enters per proposal. */
  gainShare?: {
    manualFitUpMinutes?: number;
    toolFitUpMinutes?: number;
    firingLineValuePerHour?: number;
  };
}

export interface ModuleDef {
  key: ModuleKey;
  name: string;
  rowNoun: string;
  volumeLabel: string;
  hasCapturePath: boolean;
  dataTypes: string[];
}

export const MODULE_DEFS: Record<ModuleKey, ModuleDef> = {
  materialTraceability: {
    key: 'materialTraceability',
    name: 'Material Traceability',
    rowNoun: 'Mill',
    volumeLabel: 'Total pipes',
    hasCapturePath: false,
    dataTypes: [
      'MTR', 'Chemical', 'Tensile', 'CVN Impact', 'AUT',
      'Hydrostatic', 'Dimensional', 'Hardness', 'Coating',
    ],
  },
  pipeFitter: {
    key: 'pipeFitter',
    name: 'PipeFitter',
    rowNoun: 'Fitting contractor',
    volumeLabel: 'Expected fit-ups',
    hasCapturePath: true,
    dataTypes: ['Scan point cloud', 'Fit-up report', 'Bevel geometry'],
  },
  ndt: {
    key: 'ndt',
    name: 'NDT',
    rowNoun: 'NDT vendor',
    volumeLabel: 'Inspection volume',
    hasCapturePath: false,
    dataTypes: ['RT', 'UT manual', 'PAUT', 'TOFD', 'MT', 'PT', 'VT', 'ECA reports'],
  },
  coating: {
    key: 'coating',
    name: 'Coating',
    rowNoun: 'Coating contractor',
    volumeLabel: 'Joints coated',
    hasCapturePath: true,
    dataTypes: ['Holiday detection', 'Cure / CDT', 'DFT readings', 'Visual'],
  },
};

export const isModuleKey = (key: string): key is ModuleKey =>
  (MODULE_KEYS as readonly string[]).includes(key);

export const getModuleDef = (key: string): ModuleDef | undefined =>
  isModuleKey(key) ? MODULE_DEFS[key] : undefined;

/** Build an empty onboarding row. Capture-path modules seed path C (BYOD). */
export const createSourceRow = (id: string, hasCapturePath: boolean): ModuleSourceRow => {
  const row: ModuleSourceRow = {
    id,
    name: '',
    sourceArrivalType: 'spreadsheet',
    dataTypes: [],
  };
  if (hasCapturePath) {
    row.capturePath = 'C';
    row.serviceDays = 0;
    row.boltOnMachineCount = 0;
  }
  return row;
};

/** Pull the per-module blended totals out of a calculate-price breakdown. */
export const moduleTotalsFromBreakdown = (
  breakdown: { products?: Record<string, { total?: number }> } | null | undefined
): Record<string, number> => {
  const totals: Record<string, number> = {};
  const products = breakdown?.products ?? {};
  Object.entries(products).forEach(([key, product]) => {
    if (typeof product?.total === 'number') totals[key] = product.total;
  });
  return totals;
};

/** A selected module needs a positive volume before it can be priced. */
export const moduleNeedsVolume = (module: ModuleState | undefined): boolean =>
  Boolean(module?.isSelected) && !(typeof module?.volume === 'number' && module.volume > 0);

/**
 * Sum the per-mill pipe counts for Material Traceability, or undefined when no
 * mill carries a count. The MT volume (Total pipes) is derived from this sum so
 * the per-mill per-pipe charge and the volume-present validation stay in step.
 */
export const sumPipeCounts = (sources: ModuleSourceRow[] | undefined): number | undefined => {
  const counts = (sources ?? [])
    .map((s) => s.pipeCount)
    .filter((c): c is number => typeof c === 'number' && c > 0);
  if (counts.length === 0) return undefined;
  return counts.reduce((total, count) => total + count, 0);
};
